import { inject, Injectable, signal } from '@angular/core';
import { DispensedPrescription } from '../../../shared/models/treatments/dispensedPrescription.model';
import { RestApiV2Service } from '../http/rest-api-v2.service';
import { UserService } from '../user-management/user.service';
import { take } from 'rxjs';
import { IpsUserInfo, PharmaceuticalUserInfo } from '../../../shared/models/users/user.model';
import { Latency } from '../../../shared/models/treatments/latency.model';
import { Treatment } from '../../../shared/models/treatments/treatment.model';

export interface DispenserData {
  prescriptions: DispensedPrescription[];
}

export interface DispenserDashboardStats {
  prescriptionsOverTime: { date: string; count: number }[];
  prescriptionsByProfessional: { professional: string; count: number }[];
  prescriptionsByInstitution: { institution: string; count: number }[];
  prescriptionsByLaboratory: { laboratory: string; count: number }[];
  topMolecules: { molecule: string; count: number }[];
}

@Injectable({
  providedIn: 'root',
})
export class DispenserService {
  private restApiV2 = inject(RestApiV2Service);
  private userService = inject(UserService);

  data = signal<DispenserData>({prescriptions: []});

  getMyData () {
    const user = this.userService.userData();
    const response = this.restApiV2.get<{data: DispenserData }>('dispenser/get-dispensed-prescriptions', { dispenser_id: user!.id });

    response.request$
    .pipe(take(1))
    .subscribe(response => {
      this.data.set(response.data);
    });
  }

  /**
   * Type guard para verificar si admin es IpsUserInfo
   */
  private isIpsUserInfo(admin: IpsUserInfo | PharmaceuticalUserInfo): admin is IpsUserInfo {
    return 'city' in admin;
  }

  /**
   * Procesa las estadísticas del dashboard del dispensador
   */
  processDispenserDashboardStats(data: DispenserData): DispenserDashboardStats {
    if (!data.prescriptions || data.prescriptions.length === 0) {
      return {
        prescriptionsOverTime: [],
        prescriptionsByProfessional: [],
        prescriptionsByInstitution: [],
        prescriptionsByLaboratory: [],
        topMolecules: [],
      };
    }

    // Prescriptions dispensed over time (grouped by dispensing date)
    const prescriptionsByDate = new Map<string, number>();
    data.prescriptions.forEach(prescription => {
      if (prescription.dispensing_date) {
        const date = new Date(prescription.dispensing_date);
        const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
        prescriptionsByDate.set(dateKey, (prescriptionsByDate.get(dateKey) || 0) + 1);
      }
    });
    const prescriptionsOverTime = Array.from(prescriptionsByDate.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Prescriptions by professional
    const prescriptionsByProfessionalMap = new Map<number, { count: number; name: string }>();
    data.prescriptions.forEach(prescription => {
      const professionalId = prescription.professional_id;
      const existing = prescriptionsByProfessionalMap.get(professionalId);
      
      // Obtener el nombre del profesional desde treatment si está disponible
      let professionalName = `Profesional ${professionalId}`;
      if (prescription.treatment?.professional) {
        const prof = prescription.treatment.professional;
        professionalName = `${prof.first_name || ''} ${prof.last_name || ''}`.trim() || professionalName;
      }
      
      prescriptionsByProfessionalMap.set(professionalId, {
        count: (existing?.count || 0) + 1,
        name: existing?.name || professionalName,
      });
    });
    const prescriptionsByProfessional = Array.from(prescriptionsByProfessionalMap.entries())
      .map(([professionalId, data]) => ({
        professional: data.name,
        count: data.count,
      }))
      .sort((a, b) => b.count - a.count);

    // Prescriptions by institution (when admin is IpsUserInfo)
    const prescriptionsByInstitutionMap = new Map<string, number>();
    data.prescriptions.forEach(prescription => {
      if (prescription.admin) {
        const institutionName = prescription.admin.name;
        prescriptionsByInstitutionMap.set(
          institutionName,
          (prescriptionsByInstitutionMap.get(institutionName) || 0) + 1
        );
      }
    });
    const prescriptionsByInstitution = Array.from(prescriptionsByInstitutionMap.entries())
      .map(([institution, count]) => ({ institution, count }))
      .sort((a, b) => b.count - a.count);

    // Most prescribed laboratories (when admin is PharmaceuticalUserInfo)
    const prescriptionsByLaboratoryMap = new Map<string, number>();
    data.prescriptions.forEach(prescription => {
      if (prescription.admin && !this.isIpsUserInfo(prescription.admin)) {
        const laboratoryName = prescription.admin.name || `Laboratorio ${prescription.admin_id}`;
        prescriptionsByLaboratoryMap.set(
          laboratoryName,
          (prescriptionsByLaboratoryMap.get(laboratoryName) || 0) + 1
        );
      }
    });
    const prescriptionsByLaboratory = Array.from(prescriptionsByLaboratoryMap.entries())
      .map(([laboratory, count]) => ({ laboratory, count }))
      .sort((a, b) => b.count - a.count);

    // Top molecules
    const moleculesMap = new Map<string, number>();
    data.prescriptions.forEach(prescription => {
      if (prescription.molecule) {
        moleculesMap.set(
          prescription.molecule,
          (moleculesMap.get(prescription.molecule) || 0) + 1
        );
      }
    });
    const topMolecules = Array.from(moleculesMap.entries())
      .map(([molecule, count]) => ({ molecule, count }))
      .sort((a, b) => b.count - a.count);

    return {
      prescriptionsOverTime,
      prescriptionsByProfessional,
      prescriptionsByInstitution,
      prescriptionsByLaboratory,
      topMolecules,
    };
  }

  addOrUpdateLatency(latency: {latency: Latency, treatment: Treatment}) {
    const newPrescription: DispensedPrescription = {
      ...latency.latency,
      treatment: latency.treatment
    };

    this.data.update(currentData => {
      const existingIndex = currentData.prescriptions.findIndex(p => p.id === latency.latency.id);

      if (existingIndex >= 0) {
        // Actualizar si existe
        currentData.prescriptions[existingIndex] = newPrescription;
      } else {
        // Agregar si no existe
        currentData.prescriptions.push(newPrescription);
      }

      return { ...currentData };
    });
  }
}
